//libs
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router';

//actions
import { ActionTypes } from 'shared/store/listingV2/actions';

//selectors
import { getIsFetching } from 'shared/store/listingV2/selectors';

//helpers
import {
  getAppliedFiltersString,
  getParamsObject,
  getURLQuery,
  makeUrlParams,
} from 'shared/store/listingV2/helpers';

export const useFetchPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const params = getParamsObject(location.search);
  const isFetching = useSelector(getIsFetching);

  const fetchPage = useCallback(() => {
    // React 18 Fix: Check isFetching state to prevent duplicate requests
    // When multiple PageIO sentinels become visible simultaneously,
    // React 18's automatic batching can cause all callbacks to read stale state.
    // Using existing Redux isFetching state prevents unnecessary API calls.
    if (isFetching) {
      return;
    }

    const pageNumber = Number(params.p) || 1;
    const urlParams = makeUrlParams(params, { p: pageNumber + 1 });

    history.replace(`${location.pathname}?${urlParams}`);
    dispatch({ type: ActionTypes.UPDATE_PAGE_NUMBER, payload: pageNumber + 1 });
  }, [dispatch, history, location.pathname, params, isFetching]);

  return { fetchPage };
};

export const useFilters = () => {
  const lastAppliedFilterRef = useRef<string | number | boolean>('');
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const params = getParamsObject(location.search);
  const updateAppliedFilter = (appliedFilters: Record<string, string>) => {
    const urlParams = makeUrlParams(params, { f: getAppliedFiltersString(appliedFilters), p: '' });
    history.push(`${location.pathname}?${urlParams}`);
    dispatch({
      type: ActionTypes.UPDATE_APPLIED_FILTERS,
      payload: appliedFilters,
    });
  };

  useEffect(() => {
    const newFilters = getURLQuery(location.search, location.pathname);
    if (lastAppliedFilterRef.current !== newFilters?.f) {
      lastAppliedFilterRef.current = newFilters?.f;
      dispatch({
        type: ActionTypes.UPDATE_APPLIED_FILTERS,
        payload: newFilters,
      });
    }
  }, [dispatch, location.search, location.pathname]);
  return { updateAppliedFilter };
};
